import {
  Alert,
  Button,
  CircleCheckIcon,
  CircleDashedIcon,
  CircleXIcon,
  KeyRoundIcon,
  LockIcon,
  MailIcon,
  RefreshIcon,
  SendIcon,
  TextField,
} from "@strust/ui";
import { css } from "@strust/ui/css";
import { link } from "@strust/ui/recipes";
import { useState, type ReactNode, type SyntheticEvent } from "react";
import { resetFields, submitFields, useFormField } from "../../../shared/form-field.ts";
import { useIntl } from "react-intl";
import {
  forgotPasswordEmailMessage,
  forgotPasswordMessages,
  invitationMessages,
  loginMessages,
  passwordRevealMessages,
} from "../messages.ts";
import { AuthHeading, AuthLayout, AuthNote } from "./auth-layout.tsx";

const MIN_PASSWORD_LENGTH = 12;
const CODE_RE = /^\d{6}$/;

const FORM = css({ display: "flex", flexDirection: "column", gap: "4" });
const FULL = css({ width: "full" });
const HINTS = css({
  display: "flex",
  flexDirection: "column",
  gap: "2",
  m: "0",
  p: "0",
  listStyle: "none",
  textStyle: "caption",
  color: "text.2",
});
const EMAIL = css({ m: "0", textStyle: "caption", color: "text.2", wordBreak: "break-all" });
const ACTIONS = css({
  display: "flex",
  flexWrap: "wrap",
  gap: "2",
  justifyContent: "space-between",
});
const RULES = css({
  listStyle: "none",
  m: "0",
  p: "0",
  display: "flex",
  flexDirection: "column",
  gap: "1.5",
  textStyle: "caption",
});
const RULE = css({
  display: "flex",
  alignItems: "center",
  gap: "2",
  "& svg": { width: "icon.md", height: "icon.md", flexShrink: "0" },
});
const RULE_COLOR = {
  met: css({ color: "ok.fg" }),
  failed: css({ color: "deny.fg" }),
  open: css({ color: "text.2" }),
};
const SR_ONLY = css({ srOnly: true });

/** Style of the "Back to sign in" link a container renders. */
export const forgotPasswordLinkClass = link();

interface CommonProps {
  readonly pending: boolean;
  /** Refusal of the last call, already translated. */
  readonly error?: string | undefined;
  /** Link back to the sign-in screen (router link rendered by the container). */
  readonly backLink: ReactNode;
}

export type ForgotPasswordScreenProps =
  | (CommonProps & {
      readonly step: "request";
      readonly onRequest: (email: string) => void;
    })
  | (CommonProps & {
      readonly step: "confirm";
      /** The email the code was asked for (shown, sent again with the code). */
      readonly email: string;
      /** A new code was asked for on this screen. */
      readonly newCodeRequested?: boolean;
      /** The server refused the last new password as common or breached. */
      readonly commonRefused?: boolean;
      readonly onConfirm: (input: { readonly code: string; readonly newPassword: string }) => void;
      readonly onNewCode: () => void;
      readonly onOtherEmail: () => void;
    });

type RuleState = "met" | "failed" | "open";

/**
 * "Forgot password" (spec explorer-access, "[FR] Self-service password reset"): one screen, two
 * steps. First the email, answered with the same neutral confirmation whatever the account; then
 * the emailed 6-digit code and the new password, with the policy hints. Presentational: the
 * container calls the API and navigates.
 */
export function ForgotPasswordScreen(props: ForgotPasswordScreenProps) {
  const intl = useIntl();
  return (
    <AuthLayout>
      <AuthHeading
        title={intl.formatMessage(forgotPasswordMessages.title)}
        subtitle={intl.formatMessage(
          props.step === "request"
            ? forgotPasswordMessages.requestSubtitle
            : forgotPasswordMessages.confirmSubtitle,
        )}
      />
      {props.step === "request" ? <RequestStep {...props} /> : <ConfirmStep {...props} />}
      <ul className={HINTS}>
        <li>{intl.formatMessage(forgotPasswordMessages.senderHint)}</li>
        <li>{intl.formatMessage(forgotPasswordMessages.ssoHint)}</li>
        <li>{intl.formatMessage(forgotPasswordMessages.administratorNote)}</li>
      </ul>
      {props.backLink}
      <AuthNote>{intl.formatMessage(forgotPasswordMessages.twoFactorNote)}</AuthNote>
    </AuthLayout>
  );
}

function RequestStep({
  pending,
  error,
  onRequest,
}: Extract<ForgotPasswordScreenProps, { step: "request" }>) {
  const intl = useIntl();
  const email = useFormField({
    validate: (value) =>
      value.trim() === "" ? intl.formatMessage(loginMessages.emailRequired) : undefined,
  });

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!submitFields(email)) return;
    onRequest(email.value.trim());
  };

  return (
    <form onSubmit={submit} noValidate className={FORM}>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <TextField
        label={intl.formatMessage(loginMessages.email)}
        type="email"
        name="email"
        autoComplete="username"
        leadingIcon={<MailIcon />}
        {...email.inputProps}
      />
      <Button type="submit" visual="primary" className={FULL} icon={<SendIcon />} loading={pending}>
        {intl.formatMessage(forgotPasswordMessages.sendCode)}
      </Button>
    </form>
  );
}

function ConfirmStep({
  email,
  pending,
  error,
  newCodeRequested,
  commonRefused,
  onConfirm,
  onNewCode,
  onOtherEmail,
}: Extract<ForgotPasswordScreenProps, { step: "confirm" }>) {
  const intl = useIntl();
  const [submitted, setSubmitted] = useState<string | undefined>(undefined);
  const code = useFormField({
    sanitize: (raw) => raw.replace(/\s/g, ""),
    validate: (value) =>
      CODE_RE.test(value) ? undefined : intl.formatMessage(forgotPasswordMessages.codeInvalid),
  });
  const password = useFormField();
  const longEnough = Array.from(password.value).length >= MIN_PASSWORD_LENGTH;
  // The mismatch shows once the user leaves the confirmation or submits, never while typing it.
  const confirm = useFormField({
    validate: (value) =>
      value !== "" && longEnough && value !== password.value
        ? intl.formatMessage(invitationMessages.mismatch)
        : undefined,
  });
  const matches = password.value !== "" && password.value === confirm.value;
  const common: RuleState =
    commonRefused === true && password.value === submitted ? "failed" : "open";

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!submitFields(code, password, confirm) || !longEnough || !matches) return;
    setSubmitted(password.value);
    onConfirm({ code: code.value, newPassword: password.value });
    // the code never lingers after an attempt (it is single use); emptying it flags nothing
    resetFields(code);
  };

  const reveal = {
    show: intl.formatMessage(passwordRevealMessages.show),
    hide: intl.formatMessage(passwordRevealMessages.hide),
  };
  const rule = (state: RuleState, label: string) => (
    <li className={`${RULE} ${RULE_COLOR[state]}`}>
      {state === "met" ? (
        <CircleCheckIcon aria-hidden="true" />
      ) : state === "failed" ? (
        <CircleXIcon aria-hidden="true" />
      ) : (
        <CircleDashedIcon aria-hidden="true" />
      )}
      <span>{label}</span>
      <span className={SR_ONLY}>
        {intl.formatMessage(
          state === "met"
            ? invitationMessages.ruleMet
            : state === "failed"
              ? invitationMessages.ruleFailed
              : invitationMessages.ruleUnmet,
        )}
      </span>
    </li>
  );

  return (
    <form onSubmit={submit} noValidate className={FORM}>
      <Alert>
        {intl.formatMessage(
          newCodeRequested === true
            ? forgotPasswordMessages.newCodeSent
            : forgotPasswordMessages.sent,
        )}
      </Alert>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <p className={EMAIL}>{intl.formatMessage(forgotPasswordEmailMessage, { email })}</p>
      <TextField
        label={intl.formatMessage(forgotPasswordMessages.code)}
        name="one-time-code"
        autoComplete="one-time-code"
        inputMode="numeric"
        maxLength={6}
        mono
        leadingIcon={<KeyRoundIcon />}
        hint={intl.formatMessage(forgotPasswordMessages.codeHint)}
        {...code.inputProps}
      />
      <TextField
        label={intl.formatMessage(invitationMessages.password)}
        type="password"
        name="new-password"
        autoComplete="new-password"
        leadingIcon={<LockIcon />}
        revealLabels={reveal}
        hint={intl.formatMessage(invitationMessages.passwordRule)}
        {...password.inputProps}
      />
      <TextField
        label={intl.formatMessage(invitationMessages.confirm)}
        type="password"
        name="confirm-password"
        autoComplete="new-password"
        leadingIcon={<LockIcon />}
        revealLabels={reveal}
        {...confirm.inputProps}
      />
      <ul className={RULES} aria-label={intl.formatMessage(invitationMessages.rulesLabel)}>
        {rule(longEnough ? "met" : "open", intl.formatMessage(invitationMessages.ruleLength))}
        {rule(common, intl.formatMessage(invitationMessages.ruleCommon))}
        {rule(matches ? "met" : "open", intl.formatMessage(invitationMessages.ruleMatch))}
      </ul>
      <Button
        type="submit"
        visual="primary"
        className={FULL}
        icon={<LockIcon />}
        loading={pending}
        disabled={!longEnough || !matches}
      >
        {intl.formatMessage(forgotPasswordMessages.submit)}
      </Button>
      <div className={ACTIONS}>
        <Button
          visual="ghost"
          size="sm"
          icon={<RefreshIcon />}
          onClick={onNewCode}
          disabled={pending}
        >
          {intl.formatMessage(forgotPasswordMessages.newCode)}
        </Button>
        <Button
          visual="ghost"
          size="sm"
          icon={<MailIcon />}
          onClick={onOtherEmail}
          disabled={pending}
        >
          {intl.formatMessage(forgotPasswordMessages.otherEmail)}
        </Button>
      </div>
    </form>
  );
}
